import AnalyticsCover from "../components/AnalyticsCover";
import DateInput from "../components/DateInput";
import { loadAnalyticsCovers } from "../api/loadAnalyticsCovers";
import useAnalyticsData from "../hooks/useAnalyticsData";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAppData } from "../context/AppDataContext";
import { useToast } from "../context/ToastContext";
import { useModal } from "../context/ModalContext";
import { analyticsApi, analyticsPaths, analyticsRequests } from "../api/analyticsApi";
import { ApiError } from "../api/httpClient";
import { LoadingState, ErrorState } from "../components/ApiState";
import EmptyState from "../components/EmptyState";
import { ViewToggle, MetricPicker, MonthChart, TypeTrendChart, TYPE_SERIES } from "../components/Charts";
import OverallUpdates from "../components/OverallUpdates";
import DirectMessages from "../components/DirectMessages";
import CommentsSection from "../components/CommentsSection";
import { usePeriod, PeriodSelect, Stat, n, fmtDateTime, fmtDay, fmtRange } from "../utils/periodShared";
import { sortNumericRows } from "../utils/numericSort";

// Temporary UI flag: enable to restore Direct Messages in its original position.
const SHOW_DIRECT_MESSAGES = false;

// Display order required for Analytics: Reposts before Shares.
const COLS = [
  ["published_at", "Published Date", false], ["cover", "Cover", false], ["description", "Content Description", false], ["type", "Content Type", false],
  ["likes", "Likes", true], ["comments", "Comments", true], ["reposts", "Reposts", true], ["shares", "Shares", true], ["saves", "Saves", true],
];
const PERIOD_OPTIONS_NO_CUSTOM = [["this_month", "This Month"], ["2", "Last 2 Months"], ["3", "Last 3 Months"], ["6", "Last 6 Months"], ["12", "Last 12 Months"]];
const TYPE_OPTIONS = [["", "All types"], ["REEL", "Reel"], ["POST", "Post"], ["CAROUSEL", "Carousel"]];
const PROFILE_FIELDS = [["media_count", "Posts"], ["followers_count", "Followers"], ["follows_count", "Following"]];
const METRIC_KEYS = ["likes", "comments", "reposts", "shares", "saves"];
const LABEL = { likes: "Likes", comments: "Comments", reposts: "Reposts", shares: "Shares", saves: "Saves" };

function Cell({ item, k }) {
  const v = item.metrics[k];
  if (v === null || v === undefined) {
    const why = !item.has_insights ? "Insights not synchronised yet." : item.unavailable_reasons?.[k] || "Not provided by Meta for this content type or account.";
    return <span className="metrics-na" title={why}>N/A</span>;
  }
  return n(v);
}

/** Month cell: exact lifetime total of that month's posts + change vs the previous month; optional month-end snapshot. */
function MonthCell({ row, k }) {
  if (row.items === 0) return <span className="metrics-caption" style={{ margin: 0 }} title="No posts were published this month.">—</span>;
  const m = row.metrics[k];
  if (m.total === null) return <span className="metrics-na" title="Meta has not returned this metric for the posts of this month (yet).">N/A</span>;
  const c = row.change[k];
  const dir = !c ? null : c.value > 0 ? "up" : c.value < 0 ? "down" : "zero";
  const end = row.at_month_end?.[k];
  return (
    <div>
      <div title={`${m.items_with_data} of ${m.items_total} posts have this metric`}>{n(m.total)}</div>
      {m.items_with_data < m.items_total && <div className="metrics-caption" style={{ margin: 0, fontSize: 10 }}>{m.items_with_data} of {m.items_total} posts</div>}
      {c ? (
        <div className={`metrics-delta is-${dir}`} style={{ marginTop: 2, fontSize: 11 }}>
          {dir === "up" ? "▲ +" : dir === "down" ? "▼ -" : ""}{dir === "zero" ? "0" : n(Math.abs(c.value))}
          {c.percent !== null && c.value !== 0 ? ` (${c.percent > 0 ? "+" : ""}${c.percent}%)` : ""}
        </div>
      ) : null}
      {end && end.total !== null && <div className="metrics-caption" style={{ margin: "2px 0 0", fontSize: 10 }} title="Snapshot this tool recorded near the end of that month">at month end: {n(end.total)}</div>}
    </div>
  );
}


function Change({ c }) {
  if (!c) return <span className="metrics-delta is-na">Comparison unavailable</span>;
  const dir = c.value > 0 ? "up" : c.value < 0 ? "down" : "zero";
  return (
    <span className={`metrics-delta is-${dir}`} style={{ margin: 0, fontSize: 12 }}>
      {dir === "up" ? "▲ +" : dir === "down" ? "▼ -" : ""}{dir === "zero" ? "0" : n(Math.abs(c.value))}
      {c.percent !== null && c.value !== 0 ? ` (${c.percent > 0 ? "+" : ""}${c.percent}%)` : ""}
    </span>
  );
}

/** One of Follows / Unfollows for a month, with the green/red change versus the previous month. */
function FollowCountCell({ row, prev, k }) {
  if (!row) return <span className="metrics-caption" style={{ margin: 0 }}>Loading…</span>;
  if (!row.available || row[k] === null || row[k] === undefined) return <span className="metrics-na" title={row.reason || "Not available from Meta."}>N/A</span>;
  let change = null;
  if (prev && prev.available && prev[k] !== null && prev[k] !== undefined) {
    const value = row[k] - prev[k];
    change = { value, percent: prev[k] > 0 ? Math.round((value / prev[k]) * 10000) / 100 : null };
  }
  return (
    <div>
      <div>{n(row[k])}</div>
      {change && <div style={{ marginTop: 2 }}><Change c={change} /></div>}
    </div>
  );
}

/** State for the two-range comparison (From: two dates, To: two dates). */
export function useCompare(accountId) {
  const todayStr = new Date().toLocaleDateString("en-CA");
  const min = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toLocaleDateString("en-CA"); })();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState({ from_start: "", from_end: "", to_start: "", to_end: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const generation = useRef(0);
  const timer = useRef(null);
  const cancelRefresh = () => { generation.current++; clearTimeout(timer.current); timer.current = null; };
  useEffect(() => {
    const currentGeneration = generation, currentTimer = timer;
    setData(null); setBusy(false); setErr(null);
    return () => { currentGeneration.current++; clearTimeout(currentTimer.current); };
  }, [accountId]);
  const bad = (a, b, label) => (!a || !b ? `${label}: choose both dates.` : b < a ? `${label}: end date is before the start date.` : a < min ? `${label}: only the last year is available.` : b > todayStr ? `${label}: end date is in the future.` : null);
  const problem = bad(v.from_start, v.from_end, "From") || bad(v.to_start, v.to_end, "To");
  const run = async () => {
    if (problem) return;
    cancelRefresh();
    const requestId = generation.current;
    const selected = { ...v };
    setBusy(true); setErr(null);
    const retry = (attempt) => {
      timer.current = setTimeout(async () => {
        if (generation.current !== requestId) return;
        try {
          const result = await analyticsApi.compare(accountId, selected, true);
          if (generation.current !== requestId) return;
          setData(result);
          if (result.pending && attempt < 12) retry(attempt + 1);
          else if (result.pending) setData({ ...result, pending: false, refresh_delayed: true });
        } catch {
          if (generation.current === requestId) setData(current => current ? { ...current, pending: false, refresh_delayed: true } : current);
        }
      }, 10000);
    };
    try {
      const result = await analyticsApi.compare(accountId, selected);
      if (generation.current !== requestId) return;
      setData(result); setOpen(false);
      if (result.pending) retry(1);
    }
    catch (e) { if (generation.current === requestId) setErr(e instanceof ApiError ? e.message : "Comparison failed."); }
    finally { if (generation.current === requestId) setBusy(false); }
  };
  const hide = () => { cancelRefresh(); setData(null); setBusy(false); };
  const reset = () => { hide(); setV({ from_start: "", from_end: "", to_start: "", to_end: "" }); setErr(null); setResetKey(k => k + 1); };
  return { open, setOpen, v, setV, data, setData, hide, busy, err, problem, run, reset, resetKey, todayStr, min };
}

/** Small filter button + popover, placed in the Month-by-month heading row. */
export function CompareFilter({ c }) {
  const set = (k) => (e) => c.setV((x) => ({ ...x, [k]: e.target.value }));
  return (
    <>
      {c.data && <button type="button" className="metrics-link-btn" aria-label="Edit comparison date ranges" aria-expanded={c.open} onClick={() => c.setOpen(!c.open)}>Edit range</button>}
      {c.open && (
        <div className="metrics-compare-pop" role="dialog" aria-label="Compare two date ranges" onKeyDown={(e) => { if (e.key === "Escape") c.setOpen(false); }}>
          <div className="metrics-compare-row"><span>From</span>
            <DateInput key={`from-start-${c.resetKey}`} aria-label="From start date" min={c.min} max={c.todayStr} value={c.v.from_start} onChange={set("from_start")} />
            <DateInput key={`from-end-${c.resetKey}`} aria-label="From end date" min={c.v.from_start || c.min} max={c.todayStr} value={c.v.from_end} onChange={set("from_end")} />
          </div>
          <div className="metrics-compare-row"><span>To</span>
            <DateInput key={`to-start-${c.resetKey}`} aria-label="To start date" min={c.min} max={c.todayStr} value={c.v.to_start} onChange={set("to_start")} />
            <DateInput key={`to-end-${c.resetKey}`} aria-label="To end date" min={c.v.to_start || c.min} max={c.todayStr} value={c.v.to_end} onChange={set("to_end")} />
          </div>
          {c.problem && (c.v.from_start || c.v.to_start) && <div className="metrics-picker-msg is-error">{c.problem}</div>}
          {c.err && <div className="metrics-picker-msg is-error">{c.err}</div>}
          <div className="metrics-picker-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={c.reset}>Reset</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={c.run} disabled={!!c.problem || c.busy}>{c.busy ? "Comparing…" : "Compare"}</button>
          </div>
        </div>
      )}
    </>
  );
}

/** Result table for the chosen ranges (shown under the heading; dismissable). */
export function CompareResults({ c, view = "table", metric = "likes", onMetricChange }) {
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const data = c.data;
  if (!data) return null;
  const columns = [["items", "Contents"], ...METRIC_KEYS.map(k => [k, LABEL[k]]), ["follows", "Follows"], ["unfollows", "Unfollows"]];
  const value = (row, key) => key === "items" ? row.content.total_items : METRIC_KEYS.includes(key) ? row.content.lifetime_totals[key].total : row.follows.available ? row.follows[key] : null;
  const change = (key) => key === "items" ? data.change.items : METRIC_KEYS.includes(key) ? data.change.metrics[key] : data.change.follows[key];
  const rows = [{ key: "from", data: data.from }, { key: "to", data: data.to }];
  const sorted = sort.key ? sortNumericRows(rows, row => value(row.data, sort.key), sort.dir) : rows;
  return (
    <div className="metrics-compare-results">
      <div className="metrics-table-head" style={{ marginBottom: 6 }}>
        <strong>Comparison: {fmtRange(data.from.start, data.from.end)} vs {fmtRange(data.to.start, data.to.end)}</strong>
      </div>
      {view === "table" ? <div className="metrics-table-wrap">
        <table className="metrics-table an-center">
          <thead><tr><th>Month</th>{columns.map(([key, label]) => <th key={key} className="is-num" aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}><button type="button" className="metrics-sort-btn" onClick={() => setSort(s => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }))}>{label}{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}</button></th>)}</tr></thead>
          <tbody>{sorted.map(row => <tr key={row.key}>
            <td>{fmtRange(row.data.start, row.data.end)}{row.key === "from" && <span className="metrics-caption"> (baseline)</span>}</td>
            {columns.map(([key]) => <td key={key} className="is-num"><div><Stat value={value(row.data, key)} /></div>{row.key === "to" && change(key) && <div style={{ marginTop: 2 }}><Change c={change(key)} /></div>}</td>)}
          </tr>)}</tbody>
        </table>
      </div> : <>
        <MetricPicker label="Metric to plot" value={metric} onChange={onMetricChange} options={[["items", "Contents"], ...METRIC_KEYS.map(k => [k, LABEL[k]]), ["follows", "Follows & unfollows"]]} />
        <MonthChart kind={view === "bar" ? "bars" : "line"} title={`${metric === "follows" ? "Follows and Unfollows" : metric === "items" ? "Contents" : LABEL[metric]} · selected date ranges`} yLabel={metric === "follows" ? "Accounts" : "Total"}
          series={metric === "follows" ? [{ name: "Follows", color: "#1fac71" }, { name: "Unfollows", color: "#e5383b" }] : [{ name: metric === "items" ? "Contents" : LABEL[metric], color: "#1fac71" }]}
          points={rows.map(row => ({ label: row.key === "from" ? "From (baseline)" : "To", values: metric === "follows" ? [value(row.data, "follows"), value(row.data, "unfollows")] : [value(row.data, metric)] }))} />
      </>}
      {(data.pending || data.refresh_delayed) && <div className="metrics-caption">{data.pending ? "Refreshing follower insights…" : "Follower insights could not be refreshed yet. Use Edit range to try again."}</div>}
      {(!data.from.follows.available || !data.to.follows.available) && <div className="metrics-note">Follows &amp; unfollows unavailable for {!data.from.follows.available ? "the From range" : ""}{!data.from.follows.available && !data.to.follows.available ? " and " : ""}{!data.to.follows.available ? "the To range" : ""}: {data.from.follows.reason || data.to.follows.reason}</div>}
    </div>
  );
}

function Detail({ item, detail, tz, username, cover, close, observedAt }) {
  const d = detail || item;
  return (
    <div className="an-detail">
      <button className="modal-close" onClick={close} aria-label="Close"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
      {item.permalink && <a className="btn btn-secondary btn-sm an-detail-ig" href={item.permalink} target="_blank" rel="noreferrer">View on Instagram</a>}
      <div className="an-detail-head">
        {cover ? <img loading="lazy" decoding="async" className="an-detail-cover" src={cover} alt="" /> : <span className="an-detail-cover an-detail-cover-empty" aria-hidden="true" />}
        <div className="an-detail-title">
          <div className={`tag ${item.content_type === "REEL" ? "tag-reel" : "tag-post"}`}>{item.kind_label}</div>
          <h3>@{username}</h3>
          <div className="an-detail-date">{fmtDateTime(item.published_at, tz)}</div>
        </div>
      </div>
      <div className="an-detail-caption">{item.caption || "No caption"}</div>
      {observedAt && <div className="metrics-caption">Insights observed {fmtDateTime(observedAt, tz)}</div>}
      <div className="an-detail-metrics" style={{ marginBottom: 12 }}>
        {METRIC_KEYS.map((k) => (
          <div key={k} title={d.metrics[k] === null ? item.unavailable_reasons?.[k] || "Not provided by Meta for this post." : ""}>
            <span>{LABEL[k]}</span>
            <strong><Stat value={d.metrics[k]} /></strong>
          </div>
        ))}
      </div>
      {item.metric_source !== "total" && <div className="metrics-caption" style={{ margin: "0 0 12px" }}>
        {`Organic figures only. ${item.unavailable_reasons?.totals || "Instagram has not returned combined totals for this post yet."}`}
      </div>}
      {item.last_error && <div className="metrics-note">Last refresh failed: {item.last_error}. Showing the last successfully synchronised values.</div>}
      <div className="modal-actions"><button className="btn btn-primary btn-block" onClick={close}>Close</button></div>
    </div>
  );
}

export default function Analytics() {
  const { accounts, accountsLoading } = useAppData();
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const [accountId, setAccountId] = useState(() => { try { return localStorage.getItem("rr_analyticsAccount") || ""; } catch { return ""; } });
  const [analyticsObservedAt, setAnalyticsObservedAt] = useState(() => new Date().toISOString());
  const T = usePeriod(null); // Content Performance (always opens on Last Month): totals + table (with the type filter), and exports
  const [typeFilter, setTypeFilter] = useState(""); // "" = all content types
  const M = usePeriod(null, "3"); // Month-by-month comparison only (always opens on Last 3 Months)
  const [sort, setSort] = useState("published_at");
  const [dir, setDir] = useState("desc");
  const [pageNo, setPageNo] = useState(1);
  const perfRef = useRef(null);
  const lastPage = useRef(null);
  const [perfView, setPerfView] = useState("table"); // Content Performance: table | graph
  const [perfMetric, setPerfMetric] = useState("likes");
  const [monthView, setMonthView] = useState("table"); // Month-by-month: table | graph (independent of perfView)
  const [monthSort, setMonthSort] = useState({ key: null, dir: "desc" });
  const [monthMetric, setMonthMetric] = useState("likes");
  const [compareMetric, setCompareMetric] = useState("likes");
  const [coverErrors, setCoverErrors] = useState({});
  const [covers, setCovers] = useState({}); // { mediaId: tiny data URL } fetched live, kept in memory only
  const [syncing, setSyncing] = useState(false);
  const [syncSnapshot, setSyncSnapshot] = useState(null);
  const [exporting, setExporting] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const cmp = useCompare(accountId);
  const pollRef = useRef(null);
  const syncGeneration = useRef(0);
  const accountRef = useRef(accountId); accountRef.current = accountId;
  const refreshBusy = useRef(false);
  const picked = accounts.some(a => String(a.id) === String(accountId)) ? accountId : null;
  const overviewState = useAnalyticsData(picked ? analyticsPaths.overview(picked) : null);
  const gridState = useAnalyticsData(picked ? analyticsPaths.media(picked,T.rangeRef.current,pageNo,sort,dir,typeFilter) : null);
  const totalState = useAnalyticsData(picked ? analyticsPaths.followsTotal(picked,T.rangeRef.current) : null, 60000, 60000);
  const monthsState = useAnalyticsData(picked ? analyticsPaths.months(picked,M.rangeRef.current) : null);
  const trendState = useAnalyticsData(picked && perfView !== 'table' ? analyticsPaths.trend(picked,T.rangeRef.current,typeFilter) : null);
  const followsState = useAnalyticsData(picked ? analyticsPaths.follows(picked,M.rangeRef.current) : null);
  const overview = overviewState.data, grid = gridState.data, monthsData = monthsState.data, trend = trendState.data;
  const follows = followsState.data ? Object.fromEntries(followsState.data.results.map(x => [x.key,x])) : null;
  const followsTotal = totalState.data;
  const [followerRefreshing, setFollowerRefreshing] = useState(false);
  const [followerRefreshError, setFollowerRefreshError] = useState(null);
  const [followerUnavailableReason, setFollowerUnavailableReason] = useState(null);
  const [followerRefreshCycle, setFollowerRefreshCycle] = useState(0);
  useEffect(() => {
    if (!picked) return undefined;
    let cancelled = false;
    setFollowerRefreshing(true); setFollowerRefreshError(null); setFollowerUnavailableReason(null);
    analyticsApi.followsRefresh(picked, T.rangeRef.current, () => cancelled).then(result => {
      if (cancelled) return;
      if (result.error_kind) setFollowerRefreshError(result.reason || (result.error_kind === 'auth' ? 'Reconnect the Instagram account to refresh follower insights.' : 'Meta could not refresh follower insights. Please retry shortly.'));
      else if (!result.available) setFollowerUnavailableReason(result.reason || 'Meta has not returned follower insights for this period.');
      else analyticsRequests.seed(analyticsPaths.followsTotal(picked, T.rangeRef.current), result, 60000);
    }).catch(error => {
      if (!cancelled) setFollowerRefreshError(error.status === 429 ? 'Follower insights are temporarily busy. Please retry shortly.' : error.status === 0 || error.message === 'Request failed.'
        ? 'Connection interrupted. Please try refreshing again.' : error.message || 'Follower insights are temporarily unavailable.');
    }).finally(() => { if (!cancelled) setFollowerRefreshing(false); });
    return () => { cancelled = true; };
  }, [picked, T.rangeKey, followerRefreshCycle]); // eslint-disable-line react-hooks/exhaustive-deps
  const loading = overviewState.loading, error = overviewState.error;
  const gridLoading = gridState.refreshing, gridError = gridState.error;
  const loadOverview = overviewState.refresh, loadGrid = gridState.refresh, loadMonths = monthsState.refresh;
  const coverIds = (grid?.results || []).map(r => String(r.instagram_media_id)).join(',');

  useEffect(() => {
    if (!accounts.length) return;
    if (!accounts.some((a) => String(a.id) === String(accountId))) setAccountId(String(accounts[0].id));
  }, [accounts, accountId]);

  // Changing page shortens/lengthens the table, which used to leave the view stranded further down the page
  // (in Month-by-month). After a page change, bring the top of Content Performance back into view.
  useEffect(() => {
    if (!grid) return;
    if (lastPage.current !== null && lastPage.current !== grid.page && perfRef.current) perfRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    lastPage.current = grid.page;
  }, [grid?.page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ids = coverIds.split(',').filter(id => id && !covers[id] && !analyticsApi.cachedCover(accountId, id));
    if (!ids.length || !accountId) return undefined;
    let cancelled = false;
    loadAnalyticsCovers(ids, batch => analyticsApi.covers(accountId, batch), r => {
      setCovers(c => ({ ...c, ...r.covers }));
      setCoverErrors(c => ({ ...c, ...(r.errors || {}) }));
    }, () => cancelled);
    return () => { cancelled = true; };
  }, [coverIds, accountId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPageNo(1); }, [accountId, T.rangeKey, sort, dir, typeFilter]);

  const stopPolling = () => { syncGeneration.current++; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };
  useEffect(() => {
    setSyncing(false); setSyncSnapshot(null); refreshBusy.current = false; setCovers({}); setCoverErrors({});
    return stopPolling;
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps
  const watchSync = useCallback(() => {
    stopPolling(); setSyncing(true);
    const generation = syncGeneration.current, deadline = Date.now() + 60000;
    const poll = async () => {
      if (accountRef.current !== accountId || syncGeneration.current !== generation) return;
      try {
        const s = await analyticsApi.syncStatus(accountId);
        if (accountRef.current !== accountId || syncGeneration.current !== generation) return;
        setSyncSnapshot({ accountId, state: s });
        if (!s.syncing) {
          stopPolling(); setSyncing(false);
          analyticsRequests.invalidate(accountId);
          await Promise.allSettled([loadOverview(), loadGrid(), loadMonths()]);
          if (s.last_error) showToast("Unable to refresh all insights. Showing the last successfully synchronized data.");
        } else if (Date.now() >= deadline) {
          stopPolling(); setSyncing(false);
        } else {
          pollRef.current = setTimeout(poll, 5000);
        }
      } catch { if (accountRef.current === accountId && syncGeneration.current === generation) { stopPolling(); setSyncing(false); } }
    };
    poll();
  }, [accountId, loadOverview, loadGrid, loadMonths, showToast]);

  const refresh = useCallback(async (auto) => {
    if (!accountId || refreshBusy.current === accountId) return;
    if (!auto && syncing) { showToast("Analytics refresh is already in progress."); return; }
    if (!auto) {
      setAnalyticsObservedAt(new Date().toISOString());
      setFollowerRefreshCycle(n => n + 1);
    }
    const generation = syncGeneration.current;
    refreshBusy.current = accountId; setSyncing(true);
    try {
      const r = await analyticsApi.refresh(accountId, auto, T.rangeRef.current);
      if (accountRef.current !== accountId || syncGeneration.current !== generation) return;
      if (r.sync) setSyncSnapshot({ accountId, state: r.sync });
      refreshBusy.current = false;
      if (r.started || r.reason === "already_running") watchSync();
      else { setSyncing(false); if (!auto) showToast("Analytics are already up to date."); }
    } catch (err) { if (accountRef.current !== accountId || syncGeneration.current !== generation) return; setSyncing(false); if (!auto) showToast(err.status === 429 ? "Analytics is busy. Please retry refresh shortly." : err instanceof ApiError ? err.message : "Refresh failed."); }
    finally { if (refreshBusy.current === accountId && syncGeneration.current === generation) refreshBusy.current = false; }
  }, [accountId, syncing, watchSync, showToast, T.rangeRef]);

  useEffect(() => { if (accountId) { try { localStorage.setItem("rr_analyticsAccount", accountId); } catch { /* ignore */ } refresh(true); } }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doExport = async (kind) => {
    setExportOpen(false); setExporting(kind);
    try {
      if (kind === "pdf") {
        const { blob, filename } = await analyticsApi.exportPdf(accountId, T.rangeRef.current);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        showToast("PDF report downloaded.");
      } else {
        const r = await analyticsApi.exportSheets(accountId, T.rangeRef.current);
        showToast("Google Sheet created.");
        window.open(r.url, "_blank", "noopener");
      }
    } catch (err) { showToast(err instanceof ApiError ? err.message : "Export failed."); }
    finally { setExporting(""); }
  };

  const onSort = (key) => {
    if (key === "description" || key === "type" || key === "cover") return;
    if (key === sort) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(key); setDir("desc"); }
  };

  const account = accounts.find((a) => String(a.id) === String(accountId));
  const tz = overview?.period.timezone;
  const openDetail = async (item) => {
    const render = (detail) => <Detail item={item} detail={detail} tz={tz} username={account?.username} cover={covers[item.instagram_media_id] || analyticsApi.cachedCover(accountId, item.instagram_media_id)} close={closeModal} observedAt={analyticsObservedAt} />;
    openModal(render(null));
    try { openModal(render(await analyticsApi.detail(accountId, item.id))); } catch { /* keep the summary view */ }
  };

  if (accountsLoading) return <LoadingState label="Loading accounts..." />;
  if (!accounts.length) {
    return (<><div className="view-header"><div><h2>Analytics</h2></div></div>
      <EmptyState title="No Instagram accounts connected" desc="Connect an account under Instagram Accounts to see its analytics." /></>);
  }

  const sync = syncSnapshot?.accountId === accountId ? syncSnapshot.state : overview?.sync;
  const summary = grid?.summary;
  const months = monthsData?.months || [];
  const monthRows = months.map((month, index) => ({ month, previous: index > 0 ? months[index - 1] : null }));
  const sortedMonths = monthSort.key ? sortNumericRows(monthRows, ({ month }) => {
    if (monthSort.key === "items") return month.items;
    if (monthSort.key === "follows" || monthSort.key === "unfollows") return follows?.[month.key]?.available ? follows[month.key][monthSort.key] : null;
    return month.items === 0 ? null : month.metrics[monthSort.key]?.total;
  }, monthSort.dir) : monthRows;
  const sortMonth = (key) => setMonthSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const anyValues = months.some((m) => METRIC_KEYS.some((k) => m.metrics[k].total !== null));

  return (
    <>
      <div className="view-header">
        <div>
          <h2>Analytics</h2>
        </div>
      </div>

      <div className="analytics-toolbar metrics-toolbar">
        <div className="metrics-filters">
          <select className="select-filter" aria-label="Instagram account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>@{a.username}</option>)}
          </select>
        </div>
        <div className="metrics-actions">
          <span className="metrics-sync-text" aria-live="polite">
            {sync?.last_success_at ? `Last synchronised ${fmtDateTime(sync.last_success_at, tz)}` : "Not synchronised yet"}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => refresh(false)}>Refresh</button>
          <div className="metrics-export">
            <button className="btn btn-primary btn-sm" onClick={() => setExportOpen((o) => !o)} disabled={!!exporting || loading} aria-haspopup="menu" aria-expanded={exportOpen}>{exporting ? "Exporting…" : "Export"}</button>
            {exportOpen && (<div className="metrics-export-menu" role="menu">
              <button role="menuitem" onClick={() => doExport("pdf")}>Export as PDF</button>
              <button role="menuitem" onClick={() => doExport("sheets")}>Export to Google Sheets</button>
            </div>)}
          </div>
        </div>
      </div>

      {(overviewState.refreshing || gridState.refreshing || monthsState.refreshing) && <div className="metrics-caption" role="status">Refreshing...</div>}
      {(overviewState.notice || gridState.notice || monthsState.notice || totalState.notice || followsState.notice || trendState.notice) && <div className="metrics-caption" role="status">Refresh delayed. Showing the last available data.</div>}
      {(totalState.error || followsState.error) && <div className="metrics-note">Follower insights could not be refreshed. {totalState.error || followsState.error}</div>}
      {error && overview && <div className="metrics-note">Showing last available data. {error}</div>}
      {gridError && grid && <div className="metrics-note">Content refresh is delayed. Showing last available data. {gridError}</div>}
      {sync?.needs_reconnect && <div className="metrics-note">Instagram rejected this account's access: {sync.last_error} Reconnect it under Instagram Accounts. Showing the last successfully synchronised data.</div>}
      {!sync?.needs_reconnect && sync?.last_error && !syncing && <div className="metrics-note">Insights refresh is delayed: {sync.last_error}. Showing the last successfully synchronized data.</div>}
      {sync && sync.media_total > 0 && sync.media_with_insights < sync.media_total && (
        <div className="metrics-note">Insights loaded for {sync.media_with_insights} of {sync.media_total} posts and Reels. The rest load in batches — press Refresh to continue.</div>
      )}

      {loading && !overview ? <LoadingState label="Loading analytics..." /> : error && !overview ? <ErrorState title="Unable to load profile insights" message={error} onRetry={loadOverview} /> : overview && (
        <>
          {/* ---- profile totals first: Posts, Followers, Following ---- */}
          <section className="metrics-section">
            <h3 className="metrics-h3" style={{ margin: "0 0 14px" }}>Profile analysis</h3>
            <div className="metrics-profile-head">
              {account?.profile_picture_url
                ? <img className="metrics-avatar" src={account.profile_picture_url} alt="" />
                : <div className="metrics-avatar metrics-avatar-empty">{account?.username?.[0]?.toUpperCase()}</div>}
              <div>
                <div className="metrics-username">@{account?.username}</div>
                {account?.display_name && <div className="metrics-display">{account.display_name}</div>}
              </div>
            </div>
            {!overview.profile.current ? (
              <div className="metrics-note">{syncing ? "Fetching the profile statistics from Meta…" : "No profile snapshot has been recorded yet. Press Refresh to fetch it from Meta."}</div>
            ) : (
              <>
                <div className="metrics-ig-stats">
                  {PROFILE_FIELDS.map(([f, label]) => {
                    const value = overview.profile.current[f];
                    return (
                      <div key={f} className="metrics-ig-stat">
                        <div className="metrics-ig-value"><Stat value={value} /></div>
                        <div className="metrics-ig-label">{label}</div>
                        {value === null && f === "follows_count" && <div className="metrics-reason">Not exposed by the current Meta API configuration.</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="metrics-caption">Current values · updated {fmtDateTime(overview.profile.current.snapshot_at, tz)}.</div>
              </>
            )}
          </section>
        </>
      )}

          <section className="metrics-section" ref={perfRef} style={{ scrollMarginTop: 72 }}>
            <div className={`metrics-table-head${T.pickerOpen ? " has-open-picker" : ""}`}>
              <h3 className="metrics-h3" style={{ margin: 0 }}>Content performance</h3>
              <div className="metrics-table-filters">
                <select className="select-filter" aria-label="Content type" title="Filter by content type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  {TYPE_OPTIONS.map(([k, text]) => <option key={k || "all"} value={k}>{text}</option>)}
                </select>
                <div className="metrics-range-filter">
                  <PeriodSelect p={T} align="right" label="Period for the Content Performance totals and table (by publication date)" />
                  {T.period === "custom" && T.custom && <button type="button" className="metrics-link-btn" onClick={T.edit}>Edit range</button>}
                </div>
                <ViewToggle value={perfView} onChange={setPerfView} label="Content Performance view" />
              </div>
            </div>
            {summary && (
              <div style={{ margin: "14px 0 18px" }}>
              <div className="metrics-chips">
                <span>{summary.by_type.POST || 0} posts</span>
                <span>{summary.by_type.REEL || 0} reels</span>
                <span>{summary.by_type.CAROUSEL || 0} carousels</span>
              </div>
              <div className="analytics-stats metrics-totals">
                {METRIC_KEYS.map((k) => {
                  const t = summary.lifetime_totals[k];
                  return (
                    <div className="analytics-stat" key={k}>
                      <div className="analytics-stat-label">{LABEL[k]}</div>
                      <div className="analytics-stat-value"><Stat value={t.total} /></div>
                      </div>
                  );
                })}
                {[["Follows", followsTotal?.follows, followsTotal?.net === null || followsTotal?.net === undefined ? null : `Net ${followsTotal.net > 0 ? "+" : ""}${n(followsTotal.net)}`], ["Unfollows", followsTotal?.unfollows, null]].map(([label, value, sub]) => (
                  <div className="analytics-stat" key={label} title={followsTotal?.available === false ? followsTotal.reason || "" : ""}>
                    <div className="analytics-stat-label">{label}</div>
                    <div className="analytics-stat-value">{followsTotal === null ? <span className="metrics-caption" style={{ margin: 0 }}>…</span> : followsTotal.available ? n(value) : <span className="metrics-na">N/A</span>}</div>
                  </div>
                ))}
                {(() => {
                  // Net followers = follows - unfollows for the same period: green when growing, red when shrinking.
                  const ready = followsTotal && followsTotal.available;
                  const net = ready ? (followsTotal.net ?? (followsTotal.follows ?? 0) - (followsTotal.unfollows ?? 0)) : null;
                  const tone = net > 0 ? "#1fac71" : net < 0 ? "#e5383b" : undefined;
                  return (
                    <div className="analytics-stat" title={followsTotal?.available === false ? followsTotal.reason || "" : "Follows minus unfollows"}>
                      <div className="analytics-stat-label">Net Followers</div>
                      <div className="analytics-stat-value" style={tone ? { color: tone } : undefined}>
                        {followsTotal === null ? <span className="metrics-caption" style={{ margin: 0 }}>…</span> : ready ? `${net > 0 ? "+" : net < 0 ? "−" : ""}${n(Math.abs(net))}` : <span className="metrics-na">N/A</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>
              </div>
            )}
            <div className="metrics-caption metrics-performance-caption" style={{ marginTop: 0 }}>
              <span>{grid ? fmtRange(grid.period.start_date, grid.period.end_date) : "Loading…"} · Click a row for details.</span>
              {perfView === "table" && grid?.results?.some((i) => i.promoted) && <span className="metrics-paid-legend"><span className="metrics-paid-swatch" aria-hidden="true" />Grey rows were boosted or promoted as ads.</span>}
            </div>
            {followerRefreshError && <div className="metrics-note" role="status">{followerRefreshError} {followsTotal?.available ? 'Showing last available follower insights.' : 'Follower insights are currently unavailable.'}</div>}
            {totalState.refreshDelayed && <div className="metrics-caption" role="status">Follower refresh is taking longer than expected. Showing the last available data.</div>}
            {(followerUnavailableReason || followsTotal?.available === false) && !followerRefreshing && !totalState.refreshing && !totalState.error && !followerRefreshError && <div className="metrics-note">Follower insights unavailable: {followerUnavailableReason || followsTotal?.reason || "Meta has not returned data for this period."}</div>}
            {gridError && !grid ? <ErrorState title="Unable to load content insights" message={gridError} onRetry={loadGrid} /> :
              !grid || (gridLoading && !grid.results.length) ? <LoadingState label="Loading content..." /> :
                grid.results.length === 0 ? <EmptyState title="No posts or Reels found for this period." desc={sync?.media_total ? "Nothing was published in the selected dates." : "Press Refresh to fetch this account's posts from Instagram."} /> : (
                  <>
                    {perfView !== "table" && (
                      <div aria-busy={gridState.refreshing}>
                        <MetricPicker label="Metric to plot" value={perfMetric} onChange={setPerfMetric} options={METRIC_KEYS.map((k) => [k, LABEL[k]])} />
                        {!trend ? trendState.error ? <ErrorState title="Unable to load the graph" message={trendState.error} onRetry={trendState.refresh} /> : <LoadingState label="Loading graph..." /> : trend.error ? <ErrorState title="Unable to load the graph" message="The graph could not be loaded." onRetry={() => setPerfView("table")} /> : (
                          <TypeTrendChart kind={perfView === "bar" ? "bars" : "line"}
                            title={`${LABEL[perfMetric]} by content type · ${trend.granularity === "week" ? "weekly" : "monthly"}`}
                            metricLabel={LABEL[perfMetric]}
                            buckets={trend.buckets}
                            cells={Object.fromEntries(TYPE_SERIES.map((t) => [t.key, (trend.series[t.key] || []).map((c) => ({ items: c.items, total: c.metrics[perfMetric].total, n: c.metrics[perfMetric].n }))]))} />
                        )}
                      </div>
                    )}
                    {perfView === "table" && <div className="metrics-table-wrap" aria-busy={gridState.refreshing}>
                      <table className="metrics-table metrics-content-table an-center">
                        <colgroup>
                          <col className="col-date" /><col className="col-cover" /><col className="col-desc" /><col className="col-type" />
                          {METRIC_KEYS.map((k) => <col key={k} className="col-num" />)}
                        </colgroup>
                        <thead><tr>{COLS.map(([key, label, numeric]) => (
                          <th key={key} className={numeric ? "is-num" : ""} aria-sort={key === sort ? (dir === "asc" ? "ascending" : "descending") : undefined}>
                            {key === "description" || key === "type" || key === "cover" ? label : (
                              <button type="button" className="metrics-sort-btn" onClick={() => onSort(key)}>{label}{key === sort ? (dir === "asc" ? " ▲" : " ▼") : ""}</button>
                            )}
                          </th>))}</tr></thead>
                        <tbody>{grid.results.map((item) => (
                          <tr key={item.id} className={`metrics-row${item.promoted ? " is-paid" : ""}`} title={item.promoted ? "Boosted / promoted as an ad" : undefined} tabIndex={0} onClick={() => openDetail(item)} onKeyDown={(e) => { if (e.key === "Enter") openDetail(item); }}>
                            <td>{fmtDay(item.published_at, tz)}</td>
                            <td className="metrics-cover-cell"><AnalyticsCover accountId={accountId} mediaId={item.instagram_media_id} src={covers[item.instagram_media_id] || analyticsApi.cachedCover(accountId, item.instagram_media_id)} title={coverErrors[item.instagram_media_id]} /></td>
                            <td title={item.caption || "No caption"}>{item.description}</td>
                            <td>{item.kind_label}</td>
                            {METRIC_KEYS.map((k) => <td key={k} className="is-num"><Cell item={item} k={k} /></td>)}
                          </tr>))}</tbody>
                      </table>
                    </div>}
                    {grid.total > grid.page_size && (
                      <div className="metrics-pager">
                        <button className="btn btn-ghost btn-sm" disabled={grid.page <= 1} onClick={() => setPageNo(grid.page - 1)}>Previous</button>
                        <span>Page {grid.page} of {Math.ceil(grid.total / grid.page_size)}</span>
                        <button className="btn btn-ghost btn-sm" disabled={grid.page * grid.page_size >= grid.total} onClick={() => setPageNo(grid.page + 1)}>Next</button>
                      </div>
                    )}
                  </>
                )}
          </section>

          <section className="metrics-section">
            <div className={`metrics-table-head${cmp.open ? " has-open-picker" : ""}`}>
              <h3 className="metrics-h3" style={{ margin: 0 }}>Month-by-month comparison</h3>
              <div className="metrics-table-filters">
                <div className="metrics-compare-filter metrics-range-filter">
                  <select className="select-filter" aria-label="Month-by-month filter" title="Choose a month range, or compare two date ranges"
                    value={cmp.open || cmp.data ? "compare" : M.period}
                    onChange={(e) => {
                      if (e.target.value === "compare") { cmp.setOpen(true); return; }
                      cmp.setOpen(false); cmp.hide(); M.onSelect(e.target.value);
                    }}>
                    {PERIOD_OPTIONS_NO_CUSTOM.map(([k, text]) => <option key={k} value={k}>{text}</option>)}
                    <option value="compare">Compare Date Ranges</option>
                  </select>
                  <CompareFilter c={cmp} />
                </div>
                <ViewToggle value={monthView} onChange={setMonthView} label="Month-by-month comparison view" />
              </div>
            </div>
            <CompareResults c={cmp} view={monthView} metric={compareMetric} onMetricChange={setCompareMetric} />
            {!cmp.data && monthsData && !anyValues && <div className="metrics-note">No insight values for these months yet. Press Refresh to load them.</div>}
            {monthView !== "table" && !cmp.data && (
              <>
                <MetricPicker label="Metric to plot" value={monthMetric} onChange={setMonthMetric}
                  options={[...METRIC_KEYS.map((k) => [k, LABEL[k]]), ["follows", "Follows & unfollows"]]} />
                {!monthsData ? monthsState.error ? <ErrorState title="Unable to load monthly insights" message={monthsState.error} onRetry={loadMonths} /> : <LoadingState label="Loading months..." /> : monthMetric === "follows" ? (
                  <MonthChart kind={monthView === "bar" ? "bars" : "line"} title="Follows and Unfollows per month" yLabel="Accounts"
                    series={[{ name: "Follows", color: "#1fac71" }, { name: "Unfollows", color: "#e5383b" }]}
                    points={months.map((m) => ({ label: m.label, values: [follows?.[m.key]?.available ? follows[m.key].follows : null, follows?.[m.key]?.available ? follows[m.key].unfollows : null] }))} />
                ) : (
                  <TypeTrendChart kind={monthView === "bar" ? "bars" : "line"}
                    title={`${LABEL[monthMetric]} by content type · posts published each month`}
                    metricLabel={LABEL[monthMetric]}
                    buckets={months.map((m) => ({ key: m.key, label: m.label }))}
                    cells={Object.fromEntries(TYPE_SERIES.map((t) => [t.key, months.map((m) => {
                      const c = m.by_type?.[t.key];
                      return { items: c?.items || 0, total: c ? c.metrics[monthMetric].total : null, n: c ? c.metrics[monthMetric].items_with_data : 0 };
                    })]))} />
                )}
              </>
            )}
            {monthView === "table" && !cmp.data && <div className="metrics-table-wrap">
              <table className="metrics-table an-center">
                <thead><tr><th>Month</th>{[["items", "Contents"], ...METRIC_KEYS.map(k => [k, LABEL[k]]), ["follows", "Follows"], ["unfollows", "Unfollows"]].map(([key, label]) => <th key={key} className="is-num" aria-sort={monthSort.key === key ? (monthSort.dir === "asc" ? "ascending" : "descending") : undefined}><button type="button" className="metrics-sort-btn" onClick={() => sortMonth(key)}>{label}{monthSort.key === key ? (monthSort.dir === "asc" ? " ▲" : " ▼") : ""}</button></th>)}</tr></thead>
                <tbody>{sortedMonths.map(({ month: m, previous }) => (
                  <tr key={m.key}>
                    <td>{m.label}{m.baseline ? <span className="metrics-caption"> (baseline)</span> : m.running ? <span className="metrics-caption"> (to date)</span> : null}</td>
                    <td className="is-num">{m.items}</td>
                    {METRIC_KEYS.map((k) => <td key={k} className="is-num"><MonthCell row={m} k={k} /></td>)}
                    {["follows", "unfollows"].map((k) => (
                      <td key={k} className="is-num"><FollowCountCell row={follows ? follows[m.key] : null} prev={previous && follows ? follows[previous.key] : null} k={k} /></td>
                    ))}
                  </tr>
                ))}</tbody>
              </table>
            </div>}

          </section>

          <OverallUpdates key={`updates-${accountId}`} accountId={accountId} />

          {SHOW_DIRECT_MESSAGES && <DirectMessages accountId={accountId} />}

          <CommentsSection key={`comments-${accountId}`} accountId={accountId} />

    </>
  );
}
